import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCog, Tags, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** One-click chips + "Tax details" drawer for the counterparty of a bank row, and a supply-category picker for
 *  receipts with no catalogue match. Everything saved here updates the contact / memory and is used henceforth. */
export const CounterpartyTaxFix = ({ item, taxStatus, onChanged, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState({});
  const [cats, setCats] = useState(null);
  const cp = taxStatus?.counterparty || {};
  const needs = taxStatus?.counterparty_confirmation;
  const isVendor = cp.type === 'vendor';

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/contacts/tax-attributes-schema`).then((r) => setSchema(r.data)).catch(() => setSchema({ fields: [] }));
    setForm({ vat_registered: cp.vat_registered ?? null, non_resident: cp.non_resident ?? false, export_customer: cp.export_customer ?? false,
              wht_deducting: cp.wht_deducting ?? false, tin: cp.tin || '', entity_type: cp.entity_type || 'corporate',
              backup_withholding: cp.backup_withholding ?? false, no_abn: cp.no_abn ?? false, wht_exempt: cp.wht_exempt ?? false, small_company: cp.small_company ?? false });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (body) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bank-feeds/confirm-counterparty/${item.id}`, body);
      toast.success(r.data?.applied ? `Saved on ${cp.name || 'the contact'} · tax ${r.data.applied.action || 'updated'} on this row` : `Saved on ${cp.name || 'the contact'} — used for every future transaction`);
      onChanged && onChanged(item.id, r.data?.tax_status);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the tax details');
    } finally { setBusy(false); }
  };

  const pickCategory = async (code) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bank-feeds/set-supply-category/${item.id}`, { category: code, remember: true, apply_now: true });
      toast.success(`Category set: ${r.data?.category?.label || code} — remembered for this narration`);
      onChanged && onChanged(item.id, r.data?.tax_status);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not set the category');
    } finally { setBusy(false); }
  };

  if (!taxStatus) return null;
  const showChips = !!needs && isVendor;
  const showCategory = !!taxStatus.category_pick_available;
  if (!showChips && !showCategory && !cp.name) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1 ${compact ? 'mt-0.5' : 'mt-1'}`} data-testid={`counterparty-tax-fix-${item.id}`}>
      {showChips && (
        <>
          <span className="text-[10px] text-amber-700 font-medium" data-testid={`vendor-status-prompt-${item.id}`}>
            {cp.name || 'Supplier'}: VAT status unknown →
          </span>
          <button type="button" disabled={busy} onClick={() => confirm({ vat_registered: true })}
                  className="text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  data-testid={`vendor-registered-chip-${item.id}`}>VAT-registered ✓</button>
          <button type="button" disabled={busy} onClick={() => confirm({ vat_registered: false })}
                  className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100"
                  data-testid={`vendor-not-registered-chip-${item.id}`}>Not registered</button>
          <button type="button" disabled={busy} onClick={() => confirm({ non_resident: true, vat_registered: false })}
                  className="text-[10px] px-1.5 py-0.5 rounded-full border border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100"
                  data-testid={`vendor-abroad-chip-${item.id}`}>Based abroad</button>
        </>
      )}
      {showCategory && (
        <span className="inline-flex items-center gap-1" data-testid={`category-pick-${item.id}`}>
          <Tags className="w-3 h-3 text-slate-500" />
          <Select onOpenChange={(o) => { if (o && !cats) axios.get(`${API}/bank-feeds/supply-categories`).then((r) => setCats(r.data?.categories || [])).catch(() => setCats([])); }}
                  onValueChange={pickCategory} disabled={busy}>
            <SelectTrigger className="h-6 text-[10px] w-[190px] px-2" data-testid={`category-pick-trigger-${item.id}`}>
              <SelectValue placeholder="What was sold? (tax category)" />
            </SelectTrigger>
            <SelectContent>
              {(cats || []).map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`category-option-${c.code}`}>
                  {c.label} · {c.treatment === 'standard' ? `${c.rate ?? ''}%` : c.treatment.replace('_', ' ')}
                </SelectItem>
              ))}
              {cats && cats.length === 0 && <div className="px-2 py-1 text-xs text-slate-500">No categories for this country</div>}
            </SelectContent>
          </Select>
        </span>
      )}
      {cp.name && (
        <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-[10px] text-[#283F8B] underline underline-offset-2"
                data-testid={`counterparty-tax-details-btn-${item.id}`} title="Registration, residency, TIN and WHT facts for this counterparty">
          <UserCog className="w-3 h-3" /> Tax details
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="counterparty-tax-dialog">
          <DialogHeader>
            <DialogTitle>Tax details · {cp.name || 'counterparty'}</DialogTitle>
            <DialogDescription>
              These facts decide {isVendor ? 'input tax, reverse charge and withholding on every payment to this supplier' : 'output tax (export / domestic) and WHT receivable on every receipt from this customer'}. Saved on the contact and used from now on.
            </DialogDescription>
          </DialogHeader>
          {!schema ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <Label htmlFor="cp-vat-reg">{schema.tax_label || 'VAT'} registered</Label>
                <Switch id="cp-vat-reg" checked={form.vat_registered === true} onCheckedChange={(v) => setForm({ ...form, vat_registered: v })} data-testid="cp-vat-registered-switch" />
              </div>
              {form.vat_registered !== true && isVendor && (
                <p className="text-xs text-amber-700">No input {schema.tax_label || 'VAT'} will be claimed on payments to this supplier.</p>
              )}
              <div className="flex items-center justify-between">
                <Label htmlFor="cp-nonres">Based abroad (non-resident)</Label>
                <Switch id="cp-nonres" checked={!!form.non_resident} onCheckedChange={(v) => setForm({ ...form, non_resident: v })} data-testid="cp-non-resident-switch" />
              </div>
              {form.non_resident && isVendor && <p className="text-xs text-violet-700">Reverse charge / non-resident withholding rules apply to this supplier.</p>}
              {!isVendor && (
                <>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="cp-export">Export customer (supplies leave the country)</Label>
                    <Switch id="cp-export" checked={!!form.export_customer} onCheckedChange={(v) => setForm({ ...form, export_customer: v })} data-testid="cp-export-switch" />
                  </div>
                  {schema.has_wht && (
                    <div className="flex items-center justify-between">
                      <Label htmlFor="cp-whtded">Deducts WHT from our invoices</Label>
                      <Switch id="cp-whtded" checked={!!form.wht_deducting} onCheckedChange={(v) => setForm({ ...form, wht_deducting: v })} data-testid="cp-wht-deducting-switch" />
                    </div>
                  )}
                </>
              )}
              {isVendor && (schema.fields || []).filter((f) => f.type === 'bool' && ['backup_withholding', 'no_abn', 'wht_exempt', 'small_company'].includes(f.id)).map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-3">
                  <Label htmlFor={`cp-${f.id}`} className="text-xs leading-snug">{f.label}</Label>
                  <Switch id={`cp-${f.id}`} checked={!!form[f.id]} onCheckedChange={(v) => setForm({ ...form, [f.id]: v })} data-testid={`cp-${f.id.replace(/_/g, '-')}-switch`} />
                </div>
              ))}
              <div>
                <Label htmlFor="cp-entity">Entity type</Label>
                <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                  <SelectTrigger id="cp-entity" className="h-8 mt-1" data-testid="cp-entity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{['corporate', 'individual', 'government', 'nonprofit'].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="cp-tin">Tax identification number (TIN)</Label>
                <Input id="cp-tin" value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} className="h-8 mt-1" data-testid="cp-tin-input" />
                {isVendor && !form.tin && schema.has_wht && (
                  <p className="text-xs text-amber-700 mt-1">Without a TIN some countries deduct WHT at a penalty rate (Nigeria ×2, Rwanda 15 % flat) and small-payment relief is lost.</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cp-tax-cancel">Cancel</Button>
            <Button disabled={busy || !schema} className="bg-[#108981] hover:bg-[#0d7a73]" data-testid="cp-tax-save"
                    onClick={() => confirm({ ...form, tin: form.tin || null, vat_registered: form.vat_registered === null ? null : !!form.vat_registered })}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CounterpartyTaxFix;
